import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Card, Loading } from '../../../common';
import identityService from '../../../../services/identityService';
import { CHART_COLORS } from '../../../../utils/constants';
import './Charts.css';

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * DistributionChart Component
 * Displays REAL-TIME identity type distribution as a doughnut chart
 * Data comes from backend API
 */
const DistributionChart = ({ stats, loading: statsLoading = false }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDistribution = async () => {
      setLoading(true);
      try {
        const response = await identityService.getDistribution();
        
        if (response?.success && response?.data) {
          // console.log('✅ Distribution data received:', response.data);
          setChartData(response.data);
        } else {
          console.warn('⚠️ Invalid distribution response');
          setError('Invalid response format');
        }
      } catch (err) {
        console.error('❌ Error fetching distribution:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDistribution();
  }, []);

  // Build chart data from API response
  const buildChartData = () => {
    if (!chartData?.chartData) {
      return null;
    }

    const { labels, values } = chartData.chartData;

    return {
      labels: labels,
      datasets: [
        {
          data: values,
          backgroundColor: CHART_COLORS.primary,
          borderWidth: 0,
          hoverOffset: 4
        }
      ]
    };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12,
            family: "'Inter', sans-serif"
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(26, 26, 46, 0.9)',
        titleFont: {
          size: 13,
          family: "'Inter', sans-serif"
        },
        bodyFont: {
          size: 12,
          family: "'Inter', sans-serif"
        },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((context.raw / total) * 100) : 0;
            return `${context.label}: ${context.raw.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };

  const data = buildChartData();
  const totalIdentities = chartData?.chartData?.total || 0;
  const isLoading = loading || statsLoading;

  return (
    <Card className="chart-card">
      <div className="chart-header">
        <h3 className="chart-title">Identity Distribution</h3>
        <span className="chart-subtitle">
          By identity type
        </span>
      </div>
      <div className="chart-body">
        {isLoading ? (
          <div className="chart-loading">
            <Loading size="md" text="Loading distribution..." />
          </div>
        ) : error ? (
          <div className="chart-error">
            <p>Failed to load distribution data</p>
            <small>{error}</small>
          </div>
        ) : data ? (
          <div className="chart-container doughnut-container">
            <Doughnut data={data} options={options} />
            <div className="doughnut-center">
              <span className="doughnut-value">{totalIdentities.toLocaleString()}</span>
              <span className="doughnut-label">Total</span>
            </div>
          </div>
        ) : (
          <div className="chart-empty">
            <p>No distribution data available</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default DistributionChart;